import type { QueryClient } from '@tanstack/react-query';

import { analysisKeys } from '../api/queryKeys';

// How long the page keeps polling a run that is still PROCESSING. A run that
// outlives this is treated as overdue: polling stops and the page says so, rather
// than polling a run that may never finish.
export const PROCESSING_POLL_LIMIT_MS = 30 * 60 * 1000;

// When this client started watching the run. The profile carries no run start time:
// its last-modified date is not updated when a run is dispatched, so a run started
// now would read as days old. The start is therefore recorded here, when the run is
// dispatched from this client or, for a run already in flight when the page opens,
// when the profile is first seen PROCESSING. The poll limit then counts from when the
// page began waiting on the run, not from when the server did.
const startedAtKey = (analysisProfileId: string) =>
  [...analysisKeys.all, 'processing-started-at', analysisProfileId] as const;

/**
 * Records when the run began being watched. Keeps the earlier time when one is
 * already recorded, so re-observing the same run does not restart its clock; pass
 * `restart` for a newly dispatched run.
 */
export const recordProcessingStart = (
  queryClient: QueryClient,
  analysisProfileId: string,
  { restart = false, nowMs = Date.now() }: { restart?: boolean; nowMs?: number } = {}
): void => {
  if (!restart && queryClient.getQueryData(startedAtKey(analysisProfileId)) !== undefined) return;
  queryClient.setQueryData<number>(startedAtKey(analysisProfileId), nowMs);
};

/** When the current run began being watched, or null when no run is recorded. */
export const getProcessingStartedAtMs = (
  queryClient: QueryClient,
  analysisProfileId: string
): number | null => queryClient.getQueryData<number>(startedAtKey(analysisProfileId)) ?? null;

/** Forgets the recorded start once the profile is no longer PROCESSING. */
export const clearProcessingStart = (queryClient: QueryClient, analysisProfileId: string): void => {
  queryClient.removeQueries({ queryKey: startedAtKey(analysisProfileId), exact: true });
};

/**
 * Whether the run has been watched for longer than the poll limit. False when no
 * start is recorded: polling continues rather than stopping on an unknown start.
 */
export const hasProcessingExceededLimit = (
  queryClient: QueryClient,
  analysisProfileId: string,
  nowMs: number = Date.now()
): boolean => {
  const startedAtMs = getProcessingStartedAtMs(queryClient, analysisProfileId);
  return startedAtMs !== null && nowMs - startedAtMs >= PROCESSING_POLL_LIMIT_MS;
};
